import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Heading, Text } from "@nbs/ui";
import { NbsChartContainer } from "@/features/charts/NbsChartContainer";
import { NbsHorizontalBarChart } from "@/features/charts/NbsHorizontalBarChart";
import { NbsKpiCard } from "@/features/charts/NbsKpiCard";
import { STORY_SERVICES_QUERY, STORY_SERVICES_QUERY_VARS } from "../api/documents";
import { STUDY_LANDSCAPES, type LandscapeId } from "../data/landscapes";
import {
  STORY_SERVICES_ABOUT,
  STORY_SERVICE_CATEGORIES,
  STORY_SUPPORT_DESCRIPTIONS,
  STORY_SUPPORT_LEAD,
  ecoBarsFromSeries,
  groupStoryServices,
  landCoverQueryForLandscape,
  storyEcoSeriesById,
  type StoryServiceItem,
} from "../data/services";
import { LandscapePicker } from "./LandscapePicker";
import { STORY_IMAGES, StoryMedia } from "./StoryMedia";

type StoryServicesStepProps = {
  readonly selectedId: LandscapeId;
  readonly onSelect: (id: LandscapeId) => void;
  readonly onBack: () => void;
};

export function StoryServicesStep({ selectedId, onSelect, onBack }: StoryServicesStepProps) {
  const place = STUDY_LANDSCAPES.find((p) => p.id === selectedId) ?? STUDY_LANDSCAPES[0]!;
  const placeLabel = place.label;
  const landCoverQ = landCoverQueryForLandscape(selectedId);

  const { data, loading, error, refetch } = useQuery(STORY_SERVICES_QUERY, {
    variables: {
      territoryIds: [landCoverQ.territoryId],
      dateFrom: "2005-01-01",
      dateTo: "2025-12-31",
      ecoFrom: "2014-01-01",
      ecoTo: "2014-12-31",
      ...STORY_SERVICES_QUERY_VARS,
    },
    // Avoid colliding with other story steps' dashboardKpis selection sets in the shared cache.
    fetchPolicy: "no-cache",
  });

  const grouped = useMemo(() => {
    const priorities = (data?.readingN1?.priorities ?? []) as StoryServiceItem[];
    return groupStoryServices(priorities);
  }, [data]);

  const ecoById = useMemo(
    () => storyEcoSeriesById(data as Record<string, unknown> | undefined),
    [data],
  );

  const vetValue = ecoById["nbs-ind-eco-valeur-totale-vet"]?.points?.[0]?.value ?? null;
  const vetUnit = ecoById["nbs-ind-eco-valeur-totale-vet"]?.unit ?? "$ US/ha/an";
  const usageValue = ecoById["nbs-ind-eco-valeur-usage-totale"]?.points?.[0]?.value ?? null;
  const optionValue = ecoById["nbs-ind-eco-valeur-option"]?.points?.[0]?.value ?? null;
  const nonUsageValue = ecoById["nbs-ind-eco-valeur-non-usage"]?.points?.[0]?.value ?? null;

  const formatEcoValue = (value: number | null): string =>
    value == null
      ? "—"
      : new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(value);

  const approBars = useMemo(() => ecoBarsFromSeries(ecoById, "approvisionnement"), [ecoById]);
  const regBars = useMemo(() => ecoBarsFromSeries(ecoById, "regulation"), [ecoById]);
  const socioEcoBars = useMemo(
    () => ecoBarsFromSeries(ecoById, "socio-culturel-spirituel"),
    [ecoById],
  );

  const synthesisBars = useMemo(() => {
    const rows: { label: string; value: number }[] = [];
    if (optionValue != null) rows.push({ label: "Valeur d’option", value: optionValue });
    if (usageValue != null) rows.push({ label: "Valeur d’usage", value: usageValue });
    if (nonUsageValue != null) rows.push({ label: "Valeur de non-usage", value: nonUsageValue });
    if (vetValue != null) {
      rows.push({
        label: "VET (usage + non-usage)",
        value: vetValue,
      });
    }
    return rows;
  }, [optionValue, usageValue, nonUsageValue, vetValue]);

  const kpis = data?.dashboardKpis;

  return (
    <section
      className="nbs-story-step nbs-story-dash"
      data-testid="story-services-step"
      aria-labelledby="story-services-heading"
    >
      <header className="nbs-story-dash__intro">
        <div className="nbs-story-dash__intro-title">
          <p className="nbs-story-step__eyebrow">Étape 6 · Services écosystémiques</p>
          <Heading level={1} id="story-services-heading">
            Quels services rendent ces écosystèmes ?
          </Heading>
          <p className="nbs-story-step__question">
            Quels sont les principaux services mesurés pour {placeLabel} ?
          </p>
        </div>
        <div className="nbs-story-dash__intro-aside">
          <StoryMedia
            src={STORY_IMAGES.harvest}
            alt="Récolte de maïs portée sur la tête, bénéfices tirés des écosystèmes, photo CECI"
            caption="Services rendus par les écosystèmes"
            ratio="card"
          />
          <LandscapePicker selectedId={selectedId} onSelect={onSelect} />
        </div>
      </header>

      <div className="nbs-story-services__about" data-testid="story-services-about">
        <Text className="nbs-story-step__lead">
          Les forêts, les sols et les cours d’eau contribuent à la vie quotidienne et aux activités
          des communautés. Ces bénéfices, appelés <strong>services écosystémiques</strong>, se
          répartissent en quatre catégories :
        </Text>
        <ul className="nbs-story-services__about-list">
          {STORY_SERVICES_ABOUT.map((entry) => (
            <li key={entry.id}>
              <strong>{entry.title}</strong> {entry.description}
            </li>
          ))}
        </ul>
      </div>

      {error ? (
        <div className="nbs-story-dash__error" role="alert">
          <Text>Impossible de charger les services et séries associées.</Text>
          <button type="button" className="nbs-story-step__cta" onClick={() => void refetch()}>
            Réessayer
          </button>
        </div>
      ) : null}

      <div className="nbs-story-dash__kpis" aria-label="Valeurs clés des services">
        <NbsKpiCard
          label="Valeur économique totale (VET)"
          icon="coin"
          value={vetValue}
          unit={vetUnit}
          period="2014"
        />
        <NbsKpiCard
          label="Forêts claires (évolution)"
          icon="forest"
          value={kpis?.forestClearVariationPct}
          unit="%"
          valueTone={
            kpis?.forestClearVariationPct != null
              ? kpis.forestClearVariationPct >= 0
                ? "up"
                : "down"
              : null
          }
        />
        <NbsKpiCard
          label="Zones de cultures (évolution)"
          icon="territory"
          value={kpis?.cultivatedVariationPct}
          unit="%"
          valueTone={
            kpis?.cultivatedVariationPct != null
              ? kpis.cultivatedVariationPct >= 0
                ? "up"
                : "down"
              : null
          }
        />
      </div>

      <div className="nbs-story-services__boards" data-testid="story-services-grid">
        {STORY_SERVICE_CATEGORIES.map((category) => {
          const items = grouped[category.id];
          return (
            <article
              key={category.id}
              className={`nbs-story-services__board nbs-story-services__board--charts-1${
                category.id === "approvisionnement" ? " nbs-story-services__board--wide" : ""
              }`}
              data-testid={`story-services-${category.id}`}
            >
              <header className="nbs-story-services__card-head">
                <Heading level={2}>{category.label}</Heading>
                <span className="nbs-story-services__count">
                  {category.id === "support"
                    ? "7 services · sans série économique"
                    : items.length > 0
                      ? `${items.length} service${items.length === 1 ? "" : "s"}`
                      : "lecture mesurable"}
                </span>
              </header>

              {category.id === "support" ? (
                <div
                  className="nbs-story-services__support"
                  data-testid="story-services-support-body"
                >
                  <p className="nbs-story-services__support-lead">{STORY_SUPPORT_LEAD}</p>
                  <ol className="nbs-story-services__support-list">
                    {STORY_SUPPORT_DESCRIPTIONS.map((entry, index) => (
                      <li key={entry.id}>
                        <span className="nbs-story-services__support-num" aria-hidden="true">
                          {index + 1}
                        </span>
                        <div className="nbs-story-services__support-copy">
                          <h3>
                            <span className="nbs-sr-only">{index + 1}. </span>
                            {entry.title}
                          </h3>
                          <p>{entry.description}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : items.length > 0 ? (
                <ul className="nbs-story-services__list">
                  {items.map((service) => (
                    <li key={service.id}>{service.libelle}</li>
                  ))}
                </ul>
              ) : (
                <p className="nbs-story-services__empty">{category.emptyHint}</p>
              )}

              {category.id === "approvisionnement" ? (
                <div className="nbs-story-services__charts">
                  <NbsChartContainer
                    title="Valorisation économique — approvisionnement"
                    description="Vérification anti-double comptage : chaque flux d’approvisionnement est valorisé une seule fois (Tableaux 4.11–4.14). Ces barres ne doivent pas être additionnées pour recalculer la VET."
                    source="Tableaux 4.11–4.14"
                    isLoading={loading && approBars.length === 0}
                    empty={!loading && approBars.length === 0}
                  >
                    <NbsHorizontalBarChart
                      data={approBars}
                      unit="$ US/ha/an"
                      height={420}
                      showLegend
                    />
                  </NbsChartContainer>
                </div>
              ) : null}

              {category.id === "regulation" ? (
                <div className="nbs-story-services__charts">
                  <NbsChartContainer
                    title="Valorisation économique — régulation"
                    description="Ordres de grandeur publiés pour les services de régulation."
                    source="Tableaux 4.11–4.14"
                    isLoading={loading && regBars.length === 0}
                    empty={!loading && regBars.length === 0}
                  >
                    <NbsHorizontalBarChart
                      data={regBars}
                      unit="$ US/ha/an"
                      height={280}
                      showLegend
                    />
                  </NbsChartContainer>
                </div>
              ) : null}

              {category.id === "socio-culturel-spirituel" ? (
                <div className="nbs-story-services__charts">
                  <NbsChartContainer
                    title="Valorisation — culturel"
                    description="Valeurs économiques publiées pour les services culturels et l’écotourisme."
                    source="Tableau 4.12"
                    isLoading={loading && socioEcoBars.length === 0}
                    empty={!loading && socioEcoBars.length === 0}
                  >
                    <NbsHorizontalBarChart
                      data={socioEcoBars}
                      unit="$ US/ha/an"
                      height={280}
                      showLegend
                    />
                  </NbsChartContainer>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

      <section
        className="nbs-story-services__synthesis"
        data-testid="story-services-synthesis"
        aria-labelledby="story-services-synthesis-heading"
      >
        <header className="nbs-story-services__synthesis-head">
          <Heading level={2} id="story-services-synthesis-heading">
            Valeurs économiques totales des services écosystémiques
          </Heading>
          <p className="nbs-story-services__synthesis-lead">
            Agrégats publiés du rapport (Tableau 4.14) — lecture de synthèse, sans recalcul à partir
            des graphes de détail.
          </p>
        </header>

        <div className="nbs-story-services__synthesis-layout">
          <ul className="nbs-story-services__synthesis-rubrics" aria-label="Agrégats économiques">
            <li>
              <div className="nbs-story-services__synthesis-rubric-copy">
                <h3>Valeur d’option</h3>
                <p>Utiliser ou ne pas utiliser ces services écosystémiques.</p>
              </div>
              <p className="nbs-story-services__synthesis-rubric-value">
                <strong>{formatEcoValue(optionValue)}</strong>
                <span>{vetUnit}</span>
              </p>
            </li>
            <li>
              <div className="nbs-story-services__synthesis-rubric-copy">
                <h3>Valeur d’usage</h3>
              </div>
              <p className="nbs-story-services__synthesis-rubric-value">
                <strong>{formatEcoValue(usageValue)}</strong>
                <span>{vetUnit}</span>
              </p>
            </li>
            <li>
              <div className="nbs-story-services__synthesis-rubric-copy">
                <h3>Valeur de non-usage</h3>
              </div>
              <p className="nbs-story-services__synthesis-rubric-value">
                <strong>{formatEcoValue(nonUsageValue)}</strong>
                <span>{vetUnit}</span>
              </p>
            </li>
            <li className="nbs-story-services__synthesis-rubrics-item--vet">
              <div className="nbs-story-services__synthesis-rubric-copy">
                <h3>VET (usage + non-usage)</h3>
                <p>Valeur d’usage + valeur de non-usage · Tableau 4.14 · Kounounkan · 2014</p>
              </div>
              <p className="nbs-story-services__synthesis-rubric-value">
                <strong>{formatEcoValue(vetValue)}</strong>
                <span>{vetUnit}</span>
              </p>
            </li>
          </ul>

          <NbsChartContainer
            title="Agrégats économiques publiés"
            description="Valeur d’option, d’usage, de non-usage et VET — Tableau 4.14."
            source="Tableau 4.14"
            isLoading={loading && synthesisBars.length === 0}
            empty={!loading && synthesisBars.length === 0}
          >
            <NbsHorizontalBarChart data={synthesisBars} unit="$ US/ha/an" height={260} />
          </NbsChartContainer>
        </div>
      </section>

      <div className="nbs-story-step__actions nbs-story-step__actions--end">
        <button
          type="button"
          className="nbs-story-step__cta nbs-story-step__cta--ghost"
          onClick={onBack}
        >
          Retour : ce que disent les communautés
        </button>
      </div>
    </section>
  );
}
